import { motOdometerOffer } from '../../../lib/vehicle';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { User, Car, Trash2, Mail, CheckCircle, Loader2, AlertCircle, FileText, Plus, Cloud, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { motion, AnimatePresence } from 'framer-motion';
import { VRMLookup, type FinalVehicleData, type LinkedCustomerData } from '../../../components/vehicles/VRMLookup';
import QuoteDocument from './QuoteDocument';
import { pdf } from '@react-pdf/renderer';
import { QuotePDF } from './QuotePDF';
import { MotMileageOffer } from '../../../components/vehicles/MotMileageOffer';
import EmailQuoteModal from './EmailQuoteModal';
import { urlToBase64, LOGO_MAX_PX } from '../../../lib/images';
import { errorMessage, reportError } from '../../../lib/errors';
import toast from 'react-hot-toast';
import { useConfirm } from '../../../components/ui/ConfirmProvider';
import { LineItemsEditor } from '../../../components/ui/LineItemsEditor';
import { Modal } from '../../../components/ui/Modal';

interface LineItem {
    id: string;
    type: 'part' | 'labour';
    description: string;
    quantity: number;
    unitPrice: number;
    costPrice?: number;
}

interface QuoteData {
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    vrm: string;
    make: string;
    model: string;
    colour: string;
    mileage: string;
    year: string;
    engine: string;
    fuelType: string;
    lineItems: LineItem[];
    notes: string;
}

interface SavedQuote {
    id: string;
    public_token: string;
    status: string;
    total_amount: number;
    metadata: {
        customer: { name: string; phone: string; email: string };
        vehicle: {
            vrm: string; make: string; model: string; colour?: string; mileage?: string; year?: string; engine?: string; fuelType?: string;
            /** 'mot' = the odometer at the vehicle's last MOT, never today's
             *  reading; every document prints it as "at last MOT, <date>". */
            mileageSource?: 'recorded' | 'mot';
            mileageMotDate?: string | null;
        };
        lineItems: LineItem[];
        notes?: string;
    };
    created_at: string;
    rejection_reason?: string | null;
    job_id?: string | null;
    rejected_at?: string | null;
    customer_id?: string | null;
}

// What autosave compares against. The customer link is part of it: linking a
// customer whose details are already typed in changes nothing in formData, and
// must still be saved.
const snapshotOf = (data: QuoteData, linkedCustomerId: string | null) =>
    JSON.stringify({ data, customerId: linkedCustomerId });

const initialQuoteData: QuoteData = {
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    vrm: '',
    make: '',
    model: '',
    colour: '',
    mileage: '',
    year: '',
    engine: '',
    fuelType: '',
    lineItems: [],
    notes: ''
};

type ViewMode = 'list' | 'editor';

interface QuoteBuilderProps {
    onJobCreated?: (jobId: string) => void;
    /** Opens a job in the workshop: where a converted quote's extra work is added. */
    onOpenJob?: (jobId: string) => void;
    /**
     * The editor is always a pop-up, like New Job / Diagnostic / Booking.
     * `page` (default) is the Workshop's Quotes tab: the list stays on screen
     * and the editor opens over it. `modal` is the dashboard's "New Quote"
     * quick action: no list, a blank editor straight away, and closing it
     * calls `onClose`.
     */
    variant?: 'page' | 'modal';
    onClose?: () => void;
}

export default function QuoteBuilder({ onJobCreated, onOpenJob, variant = 'page', onClose }: QuoteBuilderProps = {}) {
    const { activeOrgId, molded } = useVertical();
    const isModal = variant === 'modal';
    const [viewMode, setViewMode] = useState<ViewMode>(isModal ? 'editor' : 'list');
    const confirm = useConfirm();
    const [formData, setFormData] = useState<QuoteData>(initialQuoteData);
    const [vehicleConfirmed, setVehicleConfirmed] = useState(false);
    const [mileageFromMotOn, setMileageFromMotOn] = useState<string | null>(null);
    // The vehicle's last MOT odometer reading, OFFERED under Mileage and
    // never typed in for the user (the same rule as New Job). Left blank, the
    // quote still carries it — labelled as an MOT reading — via mileageMeta.
    const [motOffer, setMotOffer] = useState<{ miles: number; testDate: string | null } | null>(null);
    const [lookupKey, setLookupKey] = useState(0);
    const [linkedCustomer, setLinkedCustomer] = useState<LinkedCustomerData | null>(null);
    // The customer record this quote is for. Converting passes it to
    // fn_create_job_with_details so the job is filed against THIS customer.
    // Without it the RPC re-matches by email/phone, and a customer with none
    // (or none readable) got a duplicate minted on every conversion
    // (20261011150000).
    const [customerId, setCustomerId] = useState<string | null>(null);
    // Only ever adds a link: a row the database already linked is never unlinked.
    const customerLink = customerId ? { customer_id: customerId } : {};
    const [saving, setSaving] = useState(false);
    const [emailing, setEmailing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [quoteId, setQuoteId] = useState<string | null>(null);
    const [status, setStatus] = useState<'draft' | 'sent' | 'accepted' | 'rejected' | 'converted' | 'voided'>('draft');
    const [reissuing, setReissuing] = useState(false);
    const [publicToken, setPublicToken] = useState<string>('');
    // The job a converted quote became. Its scope now grows as additional work
    // on that job, never by reissuing the accepted quote.
    const [linkedJobId, setLinkedJobId] = useState<string | null>(null);

    // Autosave: persist the draft as the user edits so navigating away never
    // loses work. quoteIdRef mirrors quoteId for the debounced closure; the
    // snapshot ref suppresses redundant writes (incl. right after a load/save).
    const [autoSaveState, setAutoSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
    const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedSnapshot = useRef<string>('');
    const quoteIdRef = useRef<string | null>(null);
    // The autosave write currently running, if any. Writes are serialised
    // through it: a second save waits for the first, then writes whatever is
    // newest — it is never skipped.
    const autosaveInFlight = useRef<Promise<boolean> | null>(null);
    const closingRef = useRef(false);

    // Quotes list
    const [quotes, setQuotes] = useState<SavedQuote[]>([]);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const { settings: orgSettings } = useOrgSettings();

    // Email composer + branding for the quote PDF (mirrors the invoice flow).
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [orgBranding, setOrgBranding] = useState<any>(null);
    const [logoBase64, setLogoBase64] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!activeOrgId) return;
        supabase
            .from('organizations')
            .select('name, business_address, business_phone, business_email, vat_number, branding')
            .eq('id', activeOrgId)
            .single()
            .then(({ data }) => {
                if (!data) return;
                setOrgBranding(data);
                const logoUrl = (data as any).branding?.logo_url;
                if (logoUrl) urlToBase64(logoUrl, { maxDimension: LOGO_MAX_PX }).then((b) => setLogoBase64(b || undefined)).catch(() => {});
            });
    }, [activeOrgId]);

    // VAT rate is decided by the backend (single HMRC EDR rule), keyed on the
    // quote's calendar date. New/unsaved quotes use today's date (DB default).
    const [vatRate, setVatRate] = useState(0);

    // Line-item arithmetic is pure display math; the VAT *decision* is not.
    const subtotal = formData.lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const isVatApplicable = vatRate > 0;
    const vat = subtotal * vatRate;
    const total = subtotal + vat;

    // Load quotes list
    const loadQuotes = useCallback(async () => {
        if (!activeOrgId) return;
        setLoadingQuotes(true);
        try {
            const { data, error } = await supabase
                .from('quotes')
                .select('*')
                .eq('org_id', activeOrgId)
                // Extra work found on a job lives on that job, not in this list.
                .eq('quote_type', 'original')
                .order('created_at', { ascending: false })
                .limit(50);

            if (error) throw error;
            setQuotes(data || []);
        } catch (err) {
            console.error('Failed to load quotes:', err);
        } finally {
            setLoadingQuotes(false);
        }
    }, [activeOrgId]);

    useEffect(() => {
        if (viewMode === 'list') {
            loadQuotes();
        }
    }, [viewMode, loadQuotes]);

    // Ask the backend for the applicable VAT rate (single HMRC EDR rule).
    // Keyed on the quote's calendar date: existing quote -> its created_at;
    // new/unsaved quote -> null (DB uses current_date).
    useEffect(() => {
        if (!activeOrgId) { setVatRate(0); return; }
        const existing = quotes.find(q => q.id === quoteId);
        const docDate = existing?.created_at ? existing.created_at.slice(0, 10) : null;
        let cancelled = false;
        (async () => {
            const { data, error } = await supabase.rpc('fn_get_applicable_vat_rate', {
                p_org_id: activeOrgId,
                p_doc_date: docDate,
            });
            if (!cancelled) setVatRate(error ? 0 : Number(data) || 0);
        })();
        return () => { cancelled = true; };
    }, [activeOrgId, quoteId, quotes]);

    // The mileage a quote carries, with where it came from. A typed reading
    // is 'recorded'. One taken from the last MOT — tapped, or left blank with
    // an MOT reading on file — is 'mot' plus the test date, and every quote
    // view prints it as "at last MOT, <date>", never as today's mileage.
    const mileageMeta = useMemo(() => {
        if (formData.mileage) {
            return mileageFromMotOn !== null
                ? { mileage: formData.mileage, mileageSource: 'mot' as const, mileageMotDate: mileageFromMotOn || null }
                : { mileage: formData.mileage, mileageSource: 'recorded' as const };
        }
        if (motOffer) {
            return { mileage: String(motOffer.miles), mileageSource: 'mot' as const, mileageMotDate: motOffer.testDate };
        }
        return { mileage: '' };
    }, [formData.mileage, mileageFromMotOn, motOffer]);

    // Single source of the quote metadata shape, shared by manual save,
    // convert-to-job and autosave.
    const buildMetadata = useCallback(() => ({
        customer: {
            name: formData.customerName,
            phone: formData.customerPhone,
            email: formData.customerEmail,
        },
        vehicle: {
            vrm: formData.vrm,
            make: formData.make,
            model: formData.model,
            colour: formData.colour,
            year: formData.year,
            engine: formData.engine,
            fuelType: formData.fuelType,
            ...mileageMeta,
        },
        lineItems: formData.lineItems,
        notes: formData.notes,
    }), [formData, mileageMeta]);

    // Write the draft now if it has meaningful content that is not saved yet.
    // Only for drafts, and only once there's content — so we never spam empty
    // draft rows. Shared by the debounced autosave and by closing the editor,
    // so leaving inside the debounce window doesn't drop the last edit.
    //
    // Resolves true when the draft is safe (saved, or nothing to save) and
    // false when the write failed — closing uses that to keep the editor open
    // rather than discard work it could not save.
    const persistDraft = useCallback(async (): Promise<boolean> => {
        // Wait out a write already running (an autosave fired just before a
        // close, say) and then save the newest content, rather than skipping.
        // The guards below are synchronous, so once a waiter claims the slot no
        // other can start a write alongside it.
        while (autosaveInFlight.current) await autosaveInFlight.current;

        if (status !== 'draft' || !activeOrgId) return true;

        const hasContent = !!(formData.vrm.trim() || formData.customerName.trim() || formData.lineItems.length > 0);
        if (!hasContent) return true;

        const snapshot = snapshotOf(formData, customerId);
        if (snapshot === lastSavedSnapshot.current) return true;

        // A manual save or convert is writing and records its own snapshot.
        // Closing refuses while it runs (handleCloseEditor), so this only
        // defers the debounced autosave.
        if (saving) return true;

        const write = (async () => {
            try {
                setAutoSaveState('saving');
                const metadata = buildMetadata();
                if (quoteIdRef.current) {
                    const { error } = await supabase
                        .from('quotes')
                        .update({ metadata, total_amount: total, ...(customerId ? { customer_id: customerId } : {}) })
                        .eq('id', quoteIdRef.current);
                    if (error) throw error;
                } else {
                    const { data, error } = await supabase
                        .from('quotes')
                        .insert({ org_id: activeOrgId, metadata, total_amount: total, status: 'draft', ...(customerId ? { customer_id: customerId } : {}) })
                        .select('id, public_token')
                        .single();
                    if (error) throw error;
                    quoteIdRef.current = data.id;
                    setQuoteId(data.id);
                    setPublicToken(data.public_token);
                }
                lastSavedSnapshot.current = snapshot;
                setAutoSaveState('saved');
                return true;
            } catch (err) {
                reportError(err, 'quote-builder.autosave');
                setAutoSaveState('idle');
                return false;
            }
        })();
        autosaveInFlight.current = write;
        try {
            return await write;
        } finally {
            if (autosaveInFlight.current === write) autosaveInFlight.current = null;
        }
    }, [formData, status, activeOrgId, total, saving, buildMetadata, customerId]);

    // Debounced autosave while the editor is open.
    useEffect(() => {
        if (viewMode !== 'editor') return;
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(() => { void persistDraft(); }, 1500);
        return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    }, [viewMode, persistDraft]);

    // Load a quote for editing
    const loadQuoteForEdit = (quote: SavedQuote) => {
        setLookupKey(key => key + 1);
        setMileageFromMotOn(null);
        const savedVehicle = quote.metadata.vehicle;
        const savedFromMot = savedVehicle.mileageSource === 'mot' && !!savedVehicle.mileage;
        setMotOffer(savedFromMot ? { miles: Number(savedVehicle.mileage), testDate: savedVehicle.mileageMotDate ?? null } : null);
        const loaded: QuoteData = {
            customerName: quote.metadata.customer.name,
            customerPhone: quote.metadata.customer.phone,
            customerEmail: quote.metadata.customer.email || '',
            vrm: quote.metadata.vehicle.vrm,
            make: quote.metadata.vehicle.make,
            model: quote.metadata.vehicle.model,
            colour: quote.metadata.vehicle.colour || '',
            mileage: savedFromMot ? '' : (savedVehicle.mileage || ''),
            year: quote.metadata.vehicle?.year || '',
            engine: quote.metadata.vehicle?.engine || '',
            fuelType: quote.metadata.vehicle?.fuelType || '',
            lineItems: quote.metadata.lineItems || [],
            notes: quote.metadata.notes || ''
        };
        setFormData(loaded);
        setQuoteId(quote.id);
        setStatus(quote.status as any);
        setPublicToken(quote.public_token);
        setLinkedJobId(quote.job_id ?? null);
        setCustomerId(quote.customer_id ?? null);
        // Seed autosave refs so we don't immediately re-save the just-loaded data.
        quoteIdRef.current = quote.id;
        lastSavedSnapshot.current = snapshotOf(loaded, quote.customer_id ?? null);
        setAutoSaveState('idle');
        setViewMode('editor');
    };

    // Deep link from Diagnostics "Generate quote": /workshop?tab=quotes&quote=<id>
    // opens that freshly-created draft straight into the editor once loaded.
    const quoteParamHandled = useRef(false);
    useEffect(() => {
        if (quoteParamHandled.current || loadingQuotes) return;
        const qid = new URLSearchParams(window.location.search).get('quote');
        if (!qid) return;
        const match = quotes.find((q) => q.id === qid);
        if (match) {
            quoteParamHandled.current = true;
            loadQuoteForEdit(match);
            const url = new URL(window.location.href);
            url.searchParams.delete('quote');
            window.history.replaceState({}, '', url.pathname + url.search);
        }
    }, [quotes, loadingQuotes]);

    // === VRMLookup Callbacks ===
    const handleVehicleConfirmed = (vehicle: FinalVehicleData) => {
        setVehicleConfirmed(true);
        // A recorded mileage is used as is; otherwise the MOT reading is only
        // offered. Pre-filling it made the quote print a reading up to a year
        // old as the car's mileage, and on conversion saved it to the vehicle.
        const mileage = vehicle.mileage != null && vehicle.mileage !== '' ? String(vehicle.mileage) : '';
        setMotOffer(motOdometerOffer(vehicle));
        setMileageFromMotOn(null);
        setFormData(prev => ({
            ...prev,
            vrm: vehicle.vrm,
            make: vehicle.make || '',
            model: vehicle.model || '',
            colour: vehicle.colour || '',
            year: String(vehicle.year || vehicle.yearOfManufacture || ''),
            engine: vehicle.engine || (vehicle.engineCapacity ? `${vehicle.engineCapacity}cc` : ''),
            mileage,
            fuelType: vehicle.fuelType || vehicle.fuel_type || '',
        }));
    };

    const handleCustomerFound = (customer: LinkedCustomerData) => {
        setLinkedCustomer(customer);
        setCustomerId(customer.id);
        setFormData(prev => ({
            ...prev,
            customerName: prev.customerName || customer.full_name || '',
            customerPhone: prev.customerPhone || customer.phone || '',
            customerEmail: prev.customerEmail || customer.email || '',
        }));
    };

    // Add line item
    const addLineItem = (type: 'part' | 'labour') => {
        const newItem: LineItem = {
            id: crypto.randomUUID(),
            type,
            description: '',
            quantity: 1,
            unitPrice: type === 'labour' ? (orgSettings.default_labor_rate || 60) : 0,
            costPrice: 0
        };
        setFormData(prev => ({
            ...prev,
            lineItems: [...prev.lineItems, newItem]
        }));
    };

    // Update line item
    const updateLineItem = (id: string, updates: Partial<LineItem>) => {
        setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map(item => {
                if (item.id === id) {
                    const updatedItem = { ...item, ...updates };
                    // If costPrice was just updated and it's a part, auto-calculate unitPrice with markup
                    if (updates.costPrice !== undefined && updatedItem.type === 'part') {
                        const markup = orgSettings.default_parts_markup || 0;
                        const cost = updatedItem.costPrice || 0;
                        updatedItem.unitPrice = Number((cost * (1 + markup / 100)).toFixed(2));
                    }
                    return updatedItem;
                }
                return item;
            })
        }));
    };

    // Remove line item
    const removeLineItem = (id: string) => {
        setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.filter(item => item.id !== id)
        }));
    };

    // Delete quote
    const handleDeleteQuote = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Delete quote?', message: 'This cannot be undone.', confirmText: 'Delete', isDangerous: true })) return;

        try {
            const { error } = await supabase.from('quotes').delete().eq('id', id);
            if (error) throw error;
            setQuotes(quotes.filter(q => q.id !== id));
            // If deleting the currently edited quote, go back to list
            if (quoteId === id) handleBackToList();
        } catch (err) {
            console.error('Failed to delete quote:', err);
            toast.error('Failed to delete quote');
        }
    };

    // Save quote
    const handleSaveQuote = async () => {
        setError(null);
        setSuccessMessage(null);

        if (!formData.customerName || !formData.customerPhone || !formData.vrm) {
            setError('Customer name, phone, and VRM are required');
            return;
        }

        if (!formData.year) {
            setError('Vehicle year is required');
            return;
        }

        if (formData.lineItems.length === 0) {
            setError('Add at least one line item');
            return;
        }

        if (!activeOrgId) {
            setError('Organisation not found');
            return;
        }

        setSaving(true);

        try {
            const metadata = buildMetadata();
            // Reuse the row autosave may already have created.
            const targetId = quoteId || quoteIdRef.current;

            if (targetId) {
                const { error: updateError } = await supabase
                    .from('quotes')
                    .update({ metadata, total_amount: total, ...customerLink })
                    .eq('id', targetId);

                if (updateError) throw updateError;
                if (!quoteId) setQuoteId(targetId);
                setSuccessMessage('Quote updated successfully!');
            } else {
                const { data, error: insertError } = await supabase
                    .from('quotes')
                    .insert({
                        org_id: activeOrgId,
                        metadata,
                        total_amount: total,
                        status: 'draft',
                        ...customerLink,
                    })
                    .select('id, public_token')
                    .single();

                if (insertError) throw insertError;

                quoteIdRef.current = data.id;
                setQuoteId(data.id);
                setPublicToken(data.public_token);
                setSuccessMessage('Quote saved successfully!');
            }

            // Keep autosave tracking consistent with what we just wrote.
            lastSavedSnapshot.current = snapshotOf(formData, customerId);
            setAutoSaveState('saved');

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to save quote:', err);
            setError(errorMessage(err) || 'Failed to save quote');
        } finally {
            setSaving(false);
        }
    };

    // Mark as accepted
    const handleAcceptQuote = async () => {
        if (!quoteId) return;
        try {
            await supabase.from('quotes').update({ status: 'accepted' }).eq('id', quoteId);
            setStatus('accepted');
            setSuccessMessage('Quote accepted! You can now convert to job.');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(errorMessage(err) || 'Failed to accept quote');
        }
    };

    // Email the quote to the customer (with in-email Approve / Decline buttons).
    // Assemble the quote in the shape QuotePDF expects (mirrors invoice PDF data).
    const buildQuoteForPdf = () => {
        const b = orgBranding || {};
        const branding = (b.branding || {}) as any;
        return {
            quote_number: publicToken ? publicToken.slice(0, 8).toUpperCase() : 'DRAFT',
            created_at: new Date().toISOString(),
            branding_snapshot: {
                business_name: b.name,
                address: b.business_address,
                phone: b.business_phone,
                email: b.business_email,
                vat_number: b.vat_number,
                logo_url: branding.logo_url,
                invoice_accent_color: branding.invoice_accent_color || branding.primary_color,
                invoice_theme: branding.invoice_theme,
                invoice_footer_text: branding.quote_footer_text || branding.invoice_footer_text,
            },
            customer: { full_name: formData.customerName, email: formData.customerEmail, phone: formData.customerPhone },
            vehicle: { vrm: formData.vrm, make: formData.make, model: formData.model, engine: formData.engine, fuel_type: formData.fuelType, ...mileageMeta },
            line_items: formData.lineItems.map((li) => ({
                description: li.description,
                item_type: li.type,
                quantity: li.quantity,
                unit_price: li.unitPrice,
                line_total: li.quantity * li.unitPrice,
            })),
            subtotal,
            vat_amount: vat,
            total_gross: total,
            notes: formData.notes,
        };
    };

    const generateQuotePdfBase64 = async (): Promise<string | null> => {
        try {
            const blob = await pdf(<QuotePDF quote={buildQuoteForPdf()} logoBase64={logoBase64} />).toBlob();
            return await new Promise<string | null>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string).split(',')[1] || null);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (e) {
            console.error('Quote PDF generation failed:', e);
            return null;
        }
    };

    // Persist the quote (so the public link/token exist) then open the composer.
    const handleEmailQuote = async () => {
        setError(null);
        setSuccessMessage(null);

        if (!formData.customerEmail.trim()) {
            setError('Add a customer email address to send the quote.');
            return;
        }
        setEmailing(true);
        try {
            if (!quoteIdRef.current) {
                await handleSaveQuote();
            }
        } finally {
            setEmailing(false);
        }
        if (!quoteIdRef.current) return; // save failed / validation error already surfaced
        setShowEmailModal(true);
    };

    // Convert to Job
    const handleConvertToJob = async () => {
        setSaving(true);
        try {
            // 1. Save/Update Quote first (ensure it's accepted)
            const quoteData = {
                org_id: activeOrgId,
                ...customerLink,
                status: 'accepted',
                total_amount: total,
                metadata: {
                    customer: {
                        name: formData.customerName,
                        phone: formData.customerPhone,
                        email: formData.customerEmail
                    },
                    vehicle: {
                        vrm: formData.vrm,
                        make: formData.make,
                        model: formData.model,
                        colour: formData.colour,
                        year: formData.year,
                        engine: formData.engine,
                        fuelType: formData.fuelType,
                        ...mileageMeta,
                    },
                    lineItems: formData.lineItems,
                    notes: formData.notes
                }
            };

            let currentQuoteId = quoteId;

            if (quoteId) {
                const { error } = await supabase
                    .from('quotes')
                    .update(quoteData)
                    .eq('id', quoteId);
                if (error) throw error;
            } else {
                const { data, error } = await supabase
                    .from('quotes')
                    .insert([{ ...quoteData, public_token: crypto.randomUUID() }])
                    .select()
                    .single();
                if (error) throw error;
                currentQuoteId = data.id;
                setQuoteId(data.id);
            }

            // 2. Create Job via RPC
            const { data: jobId, error: rpcError } = await supabase.rpc('fn_create_job_with_details', {
                p_org_id: activeOrgId,
                p_customer_id: customerId,
                p_customer_name: formData.customerName,
                p_customer_phone: formData.customerPhone,
                p_customer_email: formData.customerEmail,
                p_vehicle_vrm: formData.vrm,
                p_vehicle_make: formData.make,
                p_vehicle_model: formData.model,
                p_vehicle_colour: formData.colour || null,
                p_vehicle_mileage: formData.mileage ? parseInt(formData.mileage) : null,
                p_vehicle_year: formData.year ? parseInt(formData.year) : null,
                p_vehicle_engine: formData.engine || null,
                p_vehicle_fuel_type: formData.fuelType || null,
                p_job_description: `From Quote: ${formData.notes || 'No notes'}`,
                p_line_items: formData.lineItems
            });

            if (rpcError) throw rpcError;

            // 3. Update Quote to Converted + link the job so an amended reissue
            //    can re-sync it automatically on re-approval.
            await supabase.from('quotes').update({ status: 'converted', job_id: jobId }).eq('id', currentQuoteId);

            setStatus('converted');
            setLinkedJobId(jobId ?? null);
            setSuccessMessage(`Job created! ID: ${jobId?.slice(0, 8)}...`);

            if (onJobCreated && jobId) {
                onJobCreated(jobId);
            }

            setTimeout(() => {
                setSuccessMessage(null);
                handleNewQuote();
            }, 2000);
        } catch (err) {
            console.error('Conversion failed:', err);
            setError(errorMessage(err) || 'Failed to convert to job');
        } finally {
            setSaving(false);
        }
    };



    // Amend & reissue: void this quote and clone the current (edited) contents
    // into a fresh draft that supersedes it — the user just adds their extra
    // lines then emails it again for re-approval. No re-typing.
    const handleAmendReissue = async () => {
        if (!quoteId) return;
        setError(null);
        setSuccessMessage(null);
        if (!await confirm({ title: 'Void and revise?', message: 'The current quote is voided and a new revision is created for you to send.', confirmText: 'Void and revise' })) return;

        setReissuing(true);
        try {
            const { data, error } = await supabase.rpc('fn_amend_and_reissue_quote', {
                p_quote_id: quoteId,
                p_new_metadata: buildMetadata(),
                p_new_total: total,
            });
            if (error) throw error;

            // Load the new draft into the editor so the user can add lines + email it.
            const newId = data?.new_quote_id as string;
            const newToken = data?.public_token as string;
            setQuoteId(newId);
            setPublicToken(newToken || '');
            setStatus('draft');
            quoteIdRef.current = newId;
            lastSavedSnapshot.current = snapshotOf(formData, customerId);
            setAutoSaveState('saved');
            setSuccessMessage('Reissued as a new draft — add your changes and email it for approval.');
            setTimeout(() => setSuccessMessage(null), 4000);
        } catch (err) {
            console.error('Failed to reissue quote:', err);
            setError(errorMessage(err) || 'Failed to reissue quote');
        } finally {
            setReissuing(false);
        }
    };

    // Clear autosave tracking (call when starting fresh or leaving the editor).
    const resetAutosaveTracking = () => {
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        quoteIdRef.current = null;
        lastSavedSnapshot.current = '';
        setAutoSaveState('idle');
    };

    // Reset form
    const handleNewQuote = () => {
        setLookupKey(key => key + 1);
        setFormData(initialQuoteData);
        setQuoteId(null);
        setStatus('draft');
        setPublicToken('');
        setLinkedJobId(null);
        setVehicleConfirmed(false);
        setMileageFromMotOn(null);
        setMotOffer(null);
        setLinkedCustomer(null);
        setCustomerId(null);
        setError(null);
        setSuccessMessage(null);
        resetAutosaveTracking();
        setViewMode('editor');
    };

    // Deep-link: the dashboard "+ New Quote" button lands here with
    // ?action=new_quote. Open the editor straight away (same flow as the
    // in-Workshop "NEW QUOTE" button), then strip the param so a refresh or
    // back-to-list doesn't reopen it.
    const newQuoteHandled = useRef(false);
    useEffect(() => {
        if (newQuoteHandled.current) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'new_quote') {
            newQuoteHandled.current = true;
            handleNewQuote();
            params.delete('action');
            const qs = params.toString();
            window.history.replaceState({}, '', `/workshop${qs ? `?${qs}` : ''}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Leaving the editor on purpose (close button, Escape, backdrop): save what
    // is pending first, then go back to the list — or, as a modal, close. It
    // never discards work silently: if the save fails the editor stays open
    // and the trader chooses.
    const handleCloseEditor = async () => {
        if (closingRef.current) return; // a second Escape while the first close is saving
        if (saving) {
            toast('Still saving your quote — close it again in a moment.');
            return;
        }
        closingRef.current = true;
        try {
            if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
            const saved = await persistDraft();
            if (!saved) {
                const discard = await confirm({
                    title: 'Changes not saved',
                    message: "Your latest changes couldn't be saved — check your connection. Close anyway and lose them?",
                    confirmText: 'Close without saving',
                    cancelText: 'Keep editing',
                    isDangerous: true,
                });
                if (!discard) return;
            }
            handleBackToList();
        } finally {
            closingRef.current = false;
        }
    };

    const handleBackToList = () => {
        if (isModal) {
            resetAutosaveTracking();
            onClose?.();
            return;
        }
        setViewMode('list');
        setFormData(initialQuoteData);
        setQuoteId(null);
        setStatus('draft');
        setPublicToken('');
        setLinkedJobId(null);
        setError(null);
        setSuccessMessage(null);
        resetAutosaveTracking();
    };

    // LIST VIEW — always rendered on the Quotes tab; the editor opens over it.
    const listView = (
            <div className="h-full flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-tint/10">
                    <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-accent" />
                        <div>
                            <h2 className="text-lg font-bold text-fg">Quotes</h2>
                            <p className="text-xs text-fg-4">{quotes.length} quotes</p>
                        </div>
                    </div>
                    <button
                        onClick={handleNewQuote}
                        className="px-4 py-2 fd-btn-primary font-bold text-sm rounded-lg flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        NEW QUOTE
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {loadingQuotes ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-fg-3" />
                        </div>
                    ) : quotes.length === 0 ? (
                        <div className="text-center py-12 text-fg-4">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No quotes yet</p>
                            <button onClick={handleNewQuote} className="mt-2 text-accent hover:underline">
                                Create your first quote
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {quotes.map(quote => (
                                <motion.button
                                    key={quote.id}
                                    onClick={() => loadQuoteForEdit(quote)}
                                    className="w-full text-left bg-tint/5 hover:bg-tint/10 rounded-lg p-4 transition-colors border border-transparent hover:border-accent/30"
                                    whileHover={{ scale: 1.005 }}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-bold text-fg">
                                            {quote.metadata.customer.name}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${quote.status === 'draft' ? 'bg-tint/10 text-fg-3' :
                                            quote.status === 'sent' ? 'bg-info/20 text-info' :
                                                quote.status === 'accepted' ? 'bg-success/20 text-success' :
                                                    quote.status === 'converted' ? 'bg-chart-4/20 text-chart-4' :
                                                        'bg-danger/20 text-danger'
                                            }`}>
                                            {quote.status}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="flex-1">
                                            <span className="text-fg-3 block">
                                                {quote.metadata.vehicle.vrm}
                                            </span>
                                            <span className="text-fg-4 text-xs">
                                                {quote.metadata.vehicle.make} {quote.metadata.vehicle.model}
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <span className="font-mono text-accent">
                                                £{quote.total_amount?.toFixed(2) || '0.00'}
                                            </span>
                                            <button
                                                onClick={(e) => handleDeleteQuote(e, quote.id)}
                                                className="p-1.5 text-fg-4 hover:text-danger hover:bg-danger/10 rounded transition-colors relative z-10"
                                                title="Delete Quote"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-fg-4 mt-1">
                                        {new Date(quote.created_at).toLocaleDateString('en-GB')}
                                    </p>
                                    {quote.status === 'rejected' && (
                                        <div className="mt-2 rounded-md border border-danger/20 bg-danger/5 p-2">
                                            <p className="text-[10px] font-bold uppercase tracking-wide text-danger">
                                                Reason for decline
                                            </p>
                                            <p className="text-xs text-fg-2 mt-0.5">
                                                {quote.rejection_reason?.trim()
                                                    ? quote.rejection_reason
                                                    : 'No reason provided by the customer.'}
                                            </p>
                                        </div>
                                    )}
                                </motion.button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
    );

    // EDITOR VIEW — a pop-up over the list, the same as New Job and New
    // Diagnostic on the other Workshop tabs (and New Quote on the dashboard).
    const editor = (
        <div className="h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 px-5 sm:px-6 py-4 border-b border-line shrink-0">
                <div className="flex flex-1 items-center gap-3 min-w-0">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold tracking-[-0.02em] text-fg truncate">
                            {quoteId ? 'Edit Quote' : 'New Quote'}
                        </h2>
                        <p className="text-xs text-fg-3">Create an estimate for your customer</p>
                    </div>
                </div>
                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${status === 'draft' ? 'bg-tint/10 text-fg-3' :
                    status === 'sent' ? 'bg-info/20 text-info' :
                        status === 'accepted' ? 'bg-success/20 text-success' :
                            status === 'converted' ? 'bg-chart-4/20 text-chart-4' :
                                'bg-danger/20 text-danger'
                    }`}>
                    {status}
                </span>
                <button onClick={() => { void handleCloseEditor(); }} aria-label="Close" className="fd-btn fd-btn-icon -mr-2 ml-1 shrink-0">
                    <X />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* VRM Lookup (Unified Component) */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-fg-4 uppercase tracking-wider">
                            {molded.jargon.asset_identifier || 'VRM'} <span className="text-danger">*</span>
                        </label>
                        <VRMLookup
                            key={lookupKey}
                            mileageHint="After applying, the mileage box offers the last MOT reading in one tap. Take it, or enter today's."
                            onVehicleConfirmed={handleVehicleConfirmed}
                            onVehicleCleared={() => {
                                setFormData(prev => ({ ...prev, vrm: '', make: '', model: '', colour: '', mileage: '', year: '', engine: '', fuelType: '' }));
                                setVehicleConfirmed(false);
                                setMileageFromMotOn(null);
                                setMotOffer(null);
                            }}
                            onCustomerFound={handleCustomerFound}
                            confirmButtonLabel="Apply Vehicle to Quote"
                        />
                    </div>

                    {/* Customer & Vehicle Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer Details */}
                        <div className="bg-tint/5 rounded-lg p-4 space-y-4">
                            <h3 className="text-sm font-bold text-fg-2 flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Customer Details
                                {linkedCustomer && <span className="text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded uppercase">Auto-filled</span>}
                            </h3>
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={formData.customerName}
                                    onChange={e => setFormData({ ...formData, customerName: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Full Name *"
                                />
                                <input
                                    type="tel"
                                    value={formData.customerPhone}
                                    onChange={e => setFormData({ ...formData, customerPhone: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Phone *"
                                />
                                <input
                                    type="email"
                                    value={formData.customerEmail}
                                    onChange={e => setFormData({ ...formData, customerEmail: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Email"
                                />
                            </div>
                        </div>

                        {/* Vehicle Details */}
                        <div className="bg-tint/5 rounded-lg p-4 space-y-4">
                            <h3 className="text-sm font-bold text-fg-2 flex items-center gap-2">
                                <Car className="w-4 h-4" />
                                {molded.jargon.entity_label || 'Vehicle'} Details
                                {vehicleConfirmed && <span className="text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded uppercase">Auto-filled</span>}
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    value={formData.make}
                                    onChange={e => setFormData({ ...formData, make: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Make"
                                />
                                <input
                                    type="text"
                                    value={formData.model}
                                    onChange={e => setFormData({ ...formData, model: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Model"
                                />
                                <input
                                    type="number"
                                    min="1900"
                                    max="2030"
                                    value={formData.year}
                                    onChange={e => setFormData({ ...formData, year: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm font-mono focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Year *"
                                />
                                <input
                                    type="text"
                                    value={formData.colour}
                                    onChange={e => setFormData({ ...formData, colour: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Colour"
                                />
                                <input
                                    type="number"
                                    value={formData.mileage}
                                    onChange={e => { setFormData({ ...formData, mileage: e.target.value }); setMileageFromMotOn(null); }}
                                    aria-label="Mileage"
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm font-mono focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Mileage"
                                />
                                <input
                                    type="text"
                                    value={formData.engine}
                                    onChange={e => setFormData({ ...formData, engine: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Engine"
                                />
                                <input
                                    type="text"
                                    value={formData.fuelType}
                                    onChange={e => setFormData({ ...formData, fuelType: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Fuel Type"
                                />
                            </div>
                            {mileageFromMotOn !== null ? (
                                <p className="text-xs text-fg-3">Mileage from the last MOT{mileageFromMotOn ? ` (${new Date(mileageFromMotOn).toLocaleDateString('en-GB')})` : ''}, shown on the quote as an MOT reading. Update it if the vehicle has moved since.</p>
                            ) : !formData.mileage && motOffer ? (
                                <>
                                    <MotMileageOffer
                                        miles={motOffer.miles}
                                        testDate={motOffer.testDate}
                                        onUse={() => {
                                            setFormData(prev => ({ ...prev, mileage: String(motOffer.miles) }));
                                            setMileageFromMotOn(motOffer.testDate ?? '');
                                        }}
                                    />
                                    <p className="text-xs text-fg-4">Left blank, the quote shows this reading marked "at last MOT".</p>
                                </>
                            ) : null}
                        </div>
                    </div>

                    {/* Line Items */}
                    <div className="bg-tint/5 rounded-lg p-4 space-y-4">
                        {/* One shared editor — the same component the job and
                            diagnostic screens use, so a line means the same
                            thing at every step and conversion is a copy rather
                            than a translation. It also carries the phone layout;
                            this used to be a 12-column grid at every width, which
                            at 320px gave Qty a 25px track and printed the delete
                            button on top of the line total. */}
                        <LineItemsEditor
                            items={formData.lineItems}
                            onUpdate={updateLineItem}
                            onRemove={removeLineItem}
                            onAdd={addLineItem}
                            partsMarkup={orgSettings.default_parts_markup || 0}
                        />

                        {formData.lineItems.length > 0 && (
                            <div className="border-t border-tint/10 pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-fg-3">Subtotal</span>
                                    <span className="text-fg font-mono">£{subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-fg-3">VAT {isVatApplicable ? '(20%)' : ''}</span>
                                    <span className="text-fg font-mono">£{vat.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-lg font-bold">
                                    <span className="text-fg">Total</span>
                                    <span className="text-accent font-mono">£{total.toFixed(2)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-2">
                            Notes (Optional)
                        </label>
                        <textarea
                            value={formData.notes}
                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                            rows={2}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2 text-fg text-sm focus:ring-2 focus:ring-accent outline-none resize-none"
                            placeholder="Additional notes..."
                        />
                    </div>

                    {/* Messages */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </motion.div>
                        )}
                        {successMessage && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2 text-success text-sm"
                            >
                                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                                {successMessage}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-tint/10 bg-raised">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center justify-center sm:justify-start gap-2">
                        <button
                            onClick={handleSaveQuote}
                            disabled={saving}
                            className="fd-btn fd-btn-secondary"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                            SAVE
                        </button>
                        {status === 'draft' && autoSaveState !== 'idle' && (
                            <span className="flex items-center gap-1.5 text-xs text-fg-4 self-center" aria-live="polite">
                                {autoSaveState === 'saving' ? (
                                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                                ) : (
                                    <><Cloud className="w-3.5 h-3.5 text-success" /> Draft saved</>
                                )}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        {status === 'draft' && (
                            <>
                                <button
                                    onClick={handleEmailQuote}
                                    disabled={emailing || !formData.customerEmail.trim()}
                                    title={!formData.customerEmail.trim() ? 'Add a customer email first' : 'Email this quote to the customer'}
                                    className="fd-btn fd-btn-primary"
                                >
                                    {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                    EMAIL IT
                                </button>
                                <button
                                    onClick={handleConvertToJob}
                                    disabled={saving}
                                    className="fd-btn fd-btn-go"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    CONVERT TO JOB
                                </button>
                            </>
                        )}
                        {status === 'sent' && (
                            <button
                                onClick={handleEmailQuote}
                                disabled={emailing || !formData.customerEmail.trim()}
                                title="Resend the quote email"
                                className="fd-btn fd-btn-secondary"
                            >
                                {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                RESEND EMAIL
                            </button>
                        )}
                        {status === 'sent' && (
                            <button
                                onClick={handleAcceptQuote}
                                className="fd-btn fd-btn-go"
                            >
                                <CheckCircle className="w-4 h-4" />
                                MARK AS ACCEPTED
                            </button>
                        )}
                        {status === 'accepted' && (
                            <button
                                onClick={handleConvertToJob}
                                disabled={saving}
                                className="fd-btn fd-btn-go"
                            >
                                <CheckCircle className="w-4 h-4" />
                                CONVERT TO JOB
                            </button>
                        )}
                        {status === 'converted' && linkedJobId && onOpenJob && (
                            <button
                                onClick={() => onOpenJob(linkedJobId)}
                                title="This quote is now a job. Extra work is added and approved on the job."
                                className="px-4 py-2 bg-gold-fill/15 text-gold font-bold text-sm rounded-lg hover:bg-gold-fill/25 flex items-center justify-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                ADD EXTRA WORK ON THE JOB
                            </button>
                        )}
                        {/* A converted quote is the customer's accepted agreement:
                            reissuing it would void that and rewrite the job, which
                            the database now refuses. Extra scope is additional work. */}
                        {(['sent', 'accepted', 'rejected'].includes(status) || (status === 'converted' && !linkedJobId)) && (
                            <button
                                onClick={handleAmendReissue}
                                disabled={reissuing}
                                title="Void this quote and create a revised version to send for approval"
                                className="px-4 py-2 bg-gold-fill/15 text-gold font-bold text-sm rounded-lg hover:bg-gold-fill/25 flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {reissuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                AMEND &amp; REISSUE
                            </button>
                        )}
                    </div>
                </div>

                {status === 'voided' && (
                    <div className="max-w-4xl mx-auto mt-2 text-center">
                        <span className="text-xs text-danger font-medium">This quote has been voided and replaced by a newer version.</span>
                    </div>
                )}

                {showPreview && (
                    <QuoteDocument
                        quote={{
                            metadata: {
                                customer: {
                                    name: formData.customerName,
                                    phone: formData.customerPhone,
                                    email: formData.customerEmail
                                },
                                vehicle: {
                                    vrm: formData.vrm,
                                    make: formData.make,
                                    model: formData.model,
                                    colour: formData.colour,
                                    year: formData.year,
                                    ...mileageMeta,
                                },
                                lineItems: formData.lineItems,
                                notes: formData.notes
                            },
                            total_amount: total,
                            public_token: publicToken || 'DRAFT',
                            created_at: new Date().toISOString()
                        }}
                        onClose={() => setShowPreview(false)}
                    />
                )}
                {showEmailModal && quoteIdRef.current && (
                    <EmailQuoteModal
                        isOpen={showEmailModal}
                        onClose={() => setShowEmailModal(false)}
                        customerEmail={formData.customerEmail}
                        customerName={formData.customerName}
                        quoteNumber={publicToken ? publicToken.slice(0, 8).toUpperCase() : 'DRAFT'}
                        quoteId={quoteIdRef.current}
                        total={total}
                        businessName={orgBranding?.name || 'Your Workshop'}
                        generatePdfBase64={generateQuotePdfBase64}
                        onSuccess={() => {
                            setShowEmailModal(false);
                            setStatus('sent');
                            setSuccessMessage('Quote emailed to your customer.');
                            setTimeout(() => setSuccessMessage(null), 3000);
                            loadQuotes();
                        }}
                        businessEmail={orgBranding?.business_email}
                        vrm={formData.vrm}
                        vehicleName={[formData.make, formData.model].filter(Boolean).join(' ')}
                    />
                )}
            </div>
        </div>
    );

    const editorModal = (
        <Modal isOpen onClose={() => { void handleCloseEditor(); }} editor flush closeOnEscape={!showEmailModal && !showPreview}>
            {editor}
        </Modal>
    );

    if (isModal) return editorModal;

    return (
        <>
            {listView}
            {viewMode === 'editor' && editorModal}
        </>
    );
}
